/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import {
    Component,
    OnDestroy,
    Renderer2,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';

import { combineLatest, Subject } from 'rxjs';
import { map, take } from 'rxjs/operators';

import {
    RecommendationDialogService,
} from 'ng/modules/logs/services/recommendation-dialog.service';

import * as globalL10n from 'global-l10n';

import { LogCinematicComponent } from '../../log-cinematic/log-cinematic.component';
import { ITrafficLogsStates, TrafficLogsStore } from '../traffic-logs.store';
import { TrafficLogListStore } from '../traffic-log-list/traffic-log-list.store';

import * as l10n from './traffic-log-cinematic.l10n';

import { VsAppProfileType } from '../../../logs.types';
import { TrafficLogCinematicStore } from './traffic-log-cinematic.store';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const PRINT_WIDTH = 100;
const PRINT_HEADER_WIDTH = 93;
const PRINT_SINGLE_HEADER_WIDTH = 95;

export const enum L4ConnectionType {
    SERVER = 'SERVER',
    CLIENT = 'CLIENT',
}

export const printCss = ` @media print {
    body {
        margin: 0;
        padding: 0;
        width: 100%;
        overflow: hidden !important; /* Prevent horizontal scrolling */
    }

    div[ui-view], .avi-cinematic-portal__backdrop, .root__app-level-alerts,
    avi-skip-link {
        display: none;
    }
}`;

/**
 * ID for opening Traffic Log Cinematic.
 */
export const TRAFFIC_LOG_CINEMATIC_ID = 'traffic-log-cinematic';

/**
 * @description
 *     Component used for showing a single entry of traffic log.
 *     Must be wrapped by a parent cinematic component (eg. PoolLogCinematicComponent) to provide a local scope
 *     trafficLogCinematicStore, and two global scope concrete child stores of trafficLogListStore, trafficLogsStore.
 * @author Zhiqian Liu
 */
@Component({
    selector: 'traffic-log-cinematic',
    templateUrl: 'traffic-log-cinematic.component.html',
})
export class TrafficLogCinematicComponent extends LogCinematicComponent implements OnDestroy {
    public readonly L4ConnectionType = {
        [L4ConnectionType.SERVER]: L4ConnectionType.SERVER,
        [L4ConnectionType.CLIENT]: L4ConnectionType.CLIENT,
    };

    public readonly VsAppProfileType = VsAppProfileType;

    public readonly globalL10nKeys = globalL10nKeys;
    public readonly l10nKeys = l10nKeys;

    public readonly vsName$ = this.trafficLogsStore.vsName$;
    public readonly vsAppProfileType$ = this.trafficLogsStore.vsAppProfileType$;

    public readonly log$ = this.trafficLogCinematicStore.log$;

    public readonly hasPreviousLog$ = this.trafficLogCinematicStore.hasPreviousLog$;
    public readonly hasNextLog$ = this.trafficLogCinematicStore.hasNextLog$;

    public readonly timestamp$ = this.trafficLogCinematicStore.timestamp$;

    public readonly isSignificantLog$ = this.trafficLogCinematicStore.isSignificantLog$;

    public readonly isUserDefinedLog$ = this.trafficLogCinematicStore.isUserDefinedLog$;

    public readonly logTypeLabel$ = combineLatest([
        this.isSignificantLog$,
        this.isUserDefinedLog$,
    ]).pipe(
        map(([isSignificant, isUserDefined]) => {
            if (isSignificant) {
                return this.l10nKeys.significantLabel;
            }

            if (isUserDefined) {
                return this.l10nKeys.userDefinedLabel;
            }

            return this.l10nKeys.nonSignificantLabel;
        }),
    );

    public readonly significance$ = this.trafficLogCinematicStore.significance$;

    public readonly significantLog$ = this.trafficLogCinematicStore.significantLog$;

    public readonly wafLog$ = this.trafficLogCinematicStore.wafLog$;

    public readonly wafStatus$ = this.trafficLogCinematicStore.wafStatus$;

    public readonly ssl$ = this.trafficLogCinematicStore.ssl$;

    public readonly applicationInfo$ = this.trafficLogCinematicStore.applicationInfo$;

    public readonly grpcInfo$ = this.trafficLogCinematicStore.grpcInfo$;

    public readonly httpRequestHeaders$ = this.trafficLogCinematicStore.httpRequestHeaders$;

    public readonly httpResponseHeaders$ = this.trafficLogCinematicStore.httpResponseHeaders$;

    public readonly hasAuthenticationLogs$ = this.trafficLogCinematicStore.hasAuthenticationLogs$;

    public readonly icapLog$ = this.trafficLogCinematicStore.icapLog$;

    public readonly botManagementLog$ = this.trafficLogCinematicStore.botManagementLog$;

    public readonly datascriptInfo$ = this.trafficLogCinematicStore.datascriptInfo$;

    public readonly oobDsRequestLogs$ = this.trafficLogCinematicStore.oobDsRequestLogs$;

    public readonly dnsRequestInfo$ = this.trafficLogCinematicStore.dnsRequestInfo$;

    public readonly dnsResponseInfo$ = this.trafficLogCinematicStore.dnsResponseInfo$;

    public readonly l4ClientConnectionInfo$ = this.trafficLogCinematicStore.l4ClientConnectionInfo$;

    public readonly l4ServerConnectionInfo$ = this.trafficLogCinematicStore.l4ServerConnectionInfo$;

    private readonly componentDestroySubject$ = new Subject<void>();

    // To store original widths.
    private originalWidths: Map<HTMLElement, string> = new Map();
    private eleColumnLength = 0;

    constructor(
        l10nService: L10nService,
        private readonly recommendationDialogService: RecommendationDialogService,
        private readonly renderer: Renderer2,
        public readonly trafficLogCinematicStore: TrafficLogCinematicStore,
        protected readonly trafficLogsStore: TrafficLogsStore<ITrafficLogsStates>,
        trafficLogListStore: TrafficLogListStore,
    ) {
        super(trafficLogsStore, trafficLogListStore);

        l10nService.registerSourceBundles(dictionary);

        trafficLogCinematicStore.closeCinematic$.subscribe(() => this.handleClose());
    }

    /**
     * Download the log entry as a .csv file.
     */
    public downloadLog(): void {
        this.log$
            .pipe(take(1))
            .subscribe(({ log_id: logId, service_engine: seId }) => {
                this.trafficLogsStore.downloadLogEntry({
                    logId,
                    seId,
                });
            });
    }

    /**
     * Called when the user triggers the print functionality.
     */
    public printLog(): void {
        // Dynamically load the print-specific styles
        this.addPrintStyles();

        // Modify the width of elements
        const elementSelectors = [
            '#ssl .datagrid-column',
            '#ssl .datagrid-cell',
            '.log-cinematic-section-security-bot .datagrid-column',
            '.log-cinematic-section-security-bot .datagrid-cell',
            '#out-of-band .datagrid-column',
            '#out-of-band .datagrid-cell',
        ];

        this.eleColumnLength = 0;

        // Set width for all selected elements
        elementSelectors.forEach(selector => this.setElementWidthForPrinting(selector));

        // Check for specific headers and adjust their width if necessary
        this.adjustHeaderWidthsForPrinting();

        // Trigger the print dialog after making sure styles are applied
        window.print();

        // After printing, reset the widths back to their original values
        this.resetWidthsAfterPrinting();

        // Dynamically remove the print-specific styles
        this.removePrintStyles();
    }

    public openRecommendationDialog(): void {
        combineLatest([
            this.trafficLogCinematicStore.applicationLog$,
            this.trafficLogsStore.vsUuid$,
        ])
            .pipe(take(1))
            .subscribe(
                ([{ report_timestamp: reportTimestamp, request_id: requestId }, vsUuid]) =>
                    this.recommendationDialogService.openDialog(vsUuid, reportTimestamp, requestId),
            );
    }

    /** @override */
    public ngOnDestroy(): void {
        this.componentDestroySubject$.next();
        this.componentDestroySubject$.complete();

        if (this.recommendationDialogService.isOpen()) {
            this.recommendationDialogService.closeDialog();
        }
    }

    /**
     * Add the print-specific styles to the document by creating a <link> tag.
     * and appending it to the <head> of the document.
     */
    private addPrintStyles(): void {
        const style: HTMLStyleElement = document.createElement('style');

        style.id = 'mediaPrintCss';
        style.innerHTML = printCss;
        document.head.appendChild(style);
    }

    /**
     * Modify the width of elements and store their original width.
     */
    private setElementWidthForPrinting(selector: string): void {
        const elements = document.querySelectorAll(selector);

        this.eleColumnLength = selector.includes('datagrid-column') ? elements.length : this.eleColumnLength;
        elements.forEach((element: HTMLElement) => {
            if (element) {
                // Store the original width
                this.originalWidths.set(element, element.style.width);

                // Use Renderer2 to set the inline width safely
                this.renderer.setStyle(element, 'width', `${PRINT_WIDTH / this.eleColumnLength}%`);
            }
        });
    }

    /**
     * Adjust header width based on content.
     */
    private adjustHeaderWidthsForPrinting(): void {
        const eleSections = document.querySelectorAll('#headers .log-cinematic-sub-section-wrapper');
        // Check whether sections are available.

        if (eleSections.length > 0) {
            // Iterate over the sections to avoid repetition
            eleSections.forEach((section, index) => {
                const eleColumnHeaders = section.querySelectorAll('.avi-diff-grid__column-header');
                const eleDiffColumns = section.querySelectorAll('.avi-diff-grid__diff-column');
                // Apply the header column widths for printing

                this.setHeaderColumnWidthsForPrinting(PRINT_HEADER_WIDTH, eleColumnHeaders, eleDiffColumns);
            });
        }
    }

    /**
     * Reset the widths of elements back to their original values.
     */
    private resetWidthsAfterPrinting(): void {
        this.originalWidths.forEach((originalWidth, element) => {
            this.renderer.setStyle(element, 'width', originalWidth);
        });

        // Clear the map after resetting the widths
        this.originalWidths.clear();
    }

    /**
     * Remove the print-specific styles from the document.
     */
    private removePrintStyles(): void {
        const styleElement = document.getElementById('mediaPrintCss');

        if (styleElement) {
            styleElement.remove();
        }
    }

    /**
     * Set width for column headers and diff columns.
     */
    private setHeaderColumnWidthsForPrinting(width: number, ...elementsLists: Array<NodeListOf<Element>>): void {
        let eleHeaderLength = 0;

        elementsLists.forEach((elements, index) => {
            eleHeaderLength = index === 0 ? elements.length : eleHeaderLength;
            elements.forEach((element: HTMLElement) => {
                this.originalWidths.set(element, element.style.width);

                const calcWidth = `${eleHeaderLength > 1 ? width / eleHeaderLength : PRINT_SINGLE_HEADER_WIDTH}%`;

                this.renderer.setStyle(element, 'width', calcWidth);
            });
        });
    }
}
